import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { ArrowLeft, Camera, CameraOff, CheckCircle2, XCircle, Package, MapPin, User, Calendar, Layers, Zap, Save, X, Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` },
});

// Three scan modes matching the user's requested flows:
//   • quick:   scan → auto-create uscita for 1 collo (fastest)
//   • confirm: scan → open form with prefilled info → user confirms
//   • batch:   scan → accumulate into current batch → finalize with 1 tap
const MODES = [
  { id: "quick", label: "Rapida", icon: Zap, hint: "Scan → uscita immediata di 1 collo" },
  { id: "confirm", label: "Conferma", icon: CheckCircle2, hint: "Scan → verifica dati → conferma" },
  { id: "batch", label: "Batch", icon: Layers, hint: "Scan multipli → un'unica uscita" },
];

const genBatchId = () => `batch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export default function AdminWarehouseScan() {
  const [mode, setMode] = useState("quick");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // lookup result for confirm mode
  const [processing, setProcessing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchItems, setBatchItems] = useState([]); // {seq, item_name, ts}
  const [finalizing, setFinalizing] = useState(false);
  const [batchDescription, setBatchDescription] = useState("");
  const scannerRef = useRef(null);
  const lastPayloadRef = useRef(""); // dedupe rapid re-scans
  const lastPayloadTsRef = useRef(0);

  const stopCamera = useCallback(async () => {
    const s = scannerRef.current;
    if (s) {
      try { if (s.isScanning) await s.stop(); await s.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const handlePayload = useCallback(async (payload) => {
    // debounce identical scans within 2 seconds
    const now = Date.now();
    if (payload === lastPayloadRef.current && now - lastPayloadTsRef.current < 2000) return;
    lastPayloadRef.current = payload;
    lastPayloadTsRef.current = now;

    setError("");
    // Validate quickly (BE will re-validate) — accetta il formato legacy e il
    // nuovo QR con link (che apre la scheda collo dal telefono)
    const isUrlPayload = payload.includes("mov=") && payload.includes("c=");
    if (!payload.startsWith("MV|MOV:") && !isUrlPayload) {
      setError("QR non riconosciuto");
      toast.error("QR non valido");
      return;
    }

    if (mode === "confirm") {
      setProcessing(true);
      try {
        const r = await axios.get(`${API}/admin/warehouse/qr/lookup?payload=${encodeURIComponent(payload)}`, authHeaders());
        if (r.data.already_out) {
          setError("Collo già registrato in uscita");
          toast.error("Collo già uscito");
          return;
        }
        setPreview({ ...r.data, payload });
      } catch (e) {
        setError(e?.response?.data?.detail || "Errore lookup");
      } finally {
        setProcessing(false);
      }
      return;
    }

    // quick + batch: try scan-out directly
    setProcessing(true);
    try {
      const body = { payload };
      if (mode === "batch") {
        const bid = batchId || genBatchId();
        if (!batchId) setBatchId(bid);
        body.batch_id = bid;
      }
      const r = await axios.post(`${API}/admin/warehouse/qr/scan-out`, body, authHeaders());
      toast.success(`Collo ${r.data.collo_seq || "?"} — ${r.data.item_name} uscito`);
      if (mode === "batch") {
        setBatchItems((prev) => [...prev, { seq: r.data.collo_seq, item: r.data.item_name, ts: Date.now() }]);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore";
      setError(detail);
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  }, [mode, batchId]);

  const startCamera = useCallback(async () => {
    setError("");
    if (scanning) return;
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 8, qrbox: { width: 260, height: 260 } },
        (decoded) => { handlePayload(decoded); },
        () => { /* silent decode errors */ },
      );
      setScanning(true);
    } catch (e) {
      setError("Impossibile accedere alla fotocamera. Concedi il permesso e riprova.");
    }
  }, [scanning, handlePayload]);

  const confirmPreview = async () => {
    if (!preview) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/warehouse/qr/scan-out`, { payload: preview.payload }, authHeaders());
      toast.success(`Collo ${preview.collo_seq} — ${preview.item_name} uscito`);
      setPreview(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore");
    } finally {
      setProcessing(false);
    }
  };

  const finalizeBatch = async () => {
    if (!batchId) return;
    setFinalizing(true);
    try {
      const r = await axios.post(`${API}/admin/warehouse/qr/batch/finalize`, {
        batch_id: batchId,
        description: batchDescription || "Uscita rapida da QR",
      }, authHeaders());
      toast.success(`Batch chiuso: ${r.data.total_colli} colli in uscita`);
      setBatchId(null);
      setBatchItems([]);
      setBatchDescription("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore chiusura batch");
    } finally {
      setFinalizing(false);
    }
  };

  const cancelBatch = () => {
    if (!window.confirm("I colli già scansionati sono già usciti e verranno mantenuti. Vuoi solo chiudere questa sessione batch?")) return;
    setBatchId(null);
    setBatchItems([]);
    setBatchDescription("");
    toast.info("Sessione batch chiusa");
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <div className="bg-[#09090B] text-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin/magazzino" className="text-white/80 hover:text-white" data-testid="scan-back">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold uppercase tracking-wide">Scan QR</h1>
            <p className="text-[10px] text-white/60 uppercase tracking-widest">Uscita rapida da etichette magazzino</p>
          </div>
          {scanning
            ? <button onClick={stopCamera} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 px-3 py-1.5 text-xs font-bold uppercase rounded-sm" data-testid="scan-stop"><CameraOff size={14} /> Stop</button>
            : <button onClick={startCamera} className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 text-xs font-bold uppercase rounded-sm" data-testid="scan-start"><Camera size={14} /> Avvia</button>
          }
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Mode selector */}
        <div className="grid grid-cols-3 gap-2">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => {
                  if (mode === "batch" && batchId && m.id !== "batch") {
                    if (!window.confirm("Sessione batch in corso. Cambiare modalità la lascerà aperta. Continuare?")) return;
                  }
                  setMode(m.id);
                  setError("");
                  setPreview(null);
                }}
                className={`p-3 rounded-sm border-2 text-left transition-colors ${active ? "border-[#FF5200] bg-orange-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                data-testid={`scan-mode-${m.id}`}
              >
                <div className={`flex items-center gap-1.5 font-bold text-sm ${active ? "text-[#FF5200]" : "text-[#09090B]"}`}>
                  <Icon size={14} /> {m.label}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{m.hint}</p>
              </button>
            );
          })}
        </div>

        {/* Camera view */}
        <div className="bg-black rounded-sm overflow-hidden aspect-square relative">
          <div id="qr-reader" className="w-full h-full" data-testid="qr-reader-el" />
          {!scanning && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
              <Camera size={40} className="opacity-60 mb-2" />
              <p className="text-sm">Fotocamera spenta</p>
              <p className="text-[10px] uppercase tracking-widest mt-1">Premi "Avvia" per iniziare</p>
            </div>
          )}
          {processing && (
            <div className="absolute inset-x-0 bottom-0 bg-black/80 text-white px-4 py-2 flex items-center gap-2 text-sm">
              <Loader2 size={14} className="animate-spin" /> Elaborazione…
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-sm text-sm flex items-center gap-2" data-testid="scan-error">
            <XCircle size={16} /> {error}
          </div>
        )}

        {/* Batch queue */}
        {mode === "batch" && batchId && (
          <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="batch-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Sessione Batch</p>
                <p className="font-mono text-xs text-zinc-500">{batchId}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#FF5200]" data-testid="batch-count">{batchItems.length}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Colli scansionati</p>
              </div>
            </div>
            {batchItems.length > 0 && (
              <ul className="border-t border-zinc-100 pt-2 mb-3 max-h-40 overflow-auto text-sm">
                {batchItems.map((it, i) => (
                  <li key={i} className="flex items-center gap-2 py-1 text-zinc-700 border-b border-zinc-50 last:border-none">
                    <CheckCircle2 size={12} className="text-emerald-500" />
                    <span className="font-mono text-xs">#{it.seq}</span>
                    <span className="flex-1 truncate">{it.item}</span>
                  </li>
                ))}
              </ul>
            )}
            <input
              type="text"
              value={batchDescription}
              onChange={(e) => setBatchDescription(e.target.value)}
              placeholder="Riferimento uscita (es. Spedizione Milano REF-123)"
              className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm mb-2"
              data-testid="batch-description"
            />
            <div className="flex gap-2">
              <button
                onClick={finalizeBatch}
                disabled={finalizing || batchItems.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5200] hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm disabled:opacity-50"
                data-testid="batch-finalize"
              >
                {finalizing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Chiudi Batch ({batchItems.length})
              </button>
              <button onClick={cancelBatch} className="border border-zinc-300 bg-white text-zinc-600 px-3 py-2 rounded-sm hover:bg-zinc-50" data-testid="batch-cancel" title="Chiudi sessione">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        )}

        {mode !== "batch" && (
          <div className="bg-white border border-zinc-200 rounded-sm p-4 text-center text-sm text-zinc-500">
            {mode === "quick"
              ? "Scansiona il QR di un'etichetta: l'uscita viene registrata immediatamente."
              : "Scansiona il QR: rivedrai i dati e potrai confermare o annullare."}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" data-testid="scan-preview">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="font-bold text-[#09090B]">Conferma Uscita</h3>
              <button onClick={() => setPreview(null)} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="flex items-center justify-center py-2">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#09090B]">{preview.collo_seq} / {preview.collo_total}</div>
                  <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold">Numero Collo</div>
                </div>
              </div>
              <Row icon={Package} label="Articolo" value={preview.item_name} sub={preview.item_description} />
              <Row icon={User} label="Cliente" value={preview.client_name || preview.client_email} sub={preview.client_email} />
              <Row icon={MapPin} label="Posizione" value={preview.location_code || "-"} />
              <Row icon={Calendar} label="Data ingresso" value={preview.ingress_date || "-"} />
            </div>
            <div className="px-4 py-3 border-t border-zinc-200 flex gap-2 bg-zinc-50">
              <button onClick={() => setPreview(null)} className="flex-1 border border-zinc-300 bg-white text-zinc-700 px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-sm" data-testid="preview-cancel">Annulla</button>
              <button onClick={confirmPreview} disabled={processing} className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-emerald-700 disabled:opacity-50" data-testid="preview-confirm">
                {processing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Registra Uscita
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-zinc-400 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-medium text-[#09090B] truncate">{value || "-"}</div>
        {sub && <div className="text-[11px] text-zinc-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}
